import { useCallback } from 'react';
import { useAppSelector } from '@hooks/redux';
import { selectAuthView } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';
import {
  QueryEvent,
  QueryItem,
  UpdateQueryRequest,
} from '@restore/queries/queries.types';

type SubmitData = {
  username: string | null | undefined;
  password: string | null | undefined;
  signAgreement: boolean | null | undefined;
  message: string;
  attachments: File[];
};

type UseSiteUserQueryHandlersProps = {
  ctId: string;
  siteId?: string;
  queryId?: string;
  currentAction?: QueryEvent | null;
  updating: boolean;
  updateCurrentQuery: (payload: {
    clinicalTrialId: string;
    queryId: string;
    siteId: string;
    updateData: UpdateQueryRequest;
    refreshList?: boolean;
  }) => void;
};

export const useSiteUserQueryHandlers = ({
  ctId,
  siteId,
  queryId,
  currentAction,
  updating,
  updateCurrentQuery,
}: UseSiteUserQueryHandlersProps) => {
  const { selectedRole, uuid } = useAppSelector(selectAuthView);

  const handleListQuerySubmit = useCallback(
    (event: QueryEvent, query: QueryItem, data: SubmitData) => {
      const effectiveSiteId = siteId || query.siteId;
      if (!ctId || !uuid || !query.id || !effectiveSiteId) return;

      updateCurrentQuery({
        clinicalTrialId: ctId,
        queryId: query.id,
        siteId: effectiveSiteId,
        updateData: {
          event,
          actedBy: uuid,
          message: data.message,
          attachments: data.attachments,
          ...(data.username &&
            data.password &&
            data.signAgreement && {
              userCredentials: {
                username: data.username,
                password: data.password,
                signAgreement: data.signAgreement,
              },
            }),
        },
        refreshList: true,
      });
    },
    [ctId, uuid, updateCurrentQuery, siteId]
  );

  const handleUpdateCurrentQuery = useCallback(
    (updateData: UpdateQueryRequest) => {
      if (!ctId || !queryId || !siteId) return;
      updateCurrentQuery({
        clinicalTrialId: ctId,
        queryId,
        siteId,
        updateData,
      });
    },
    [ctId, queryId, siteId, updateCurrentQuery]
  );

  const handleDetailQuerySubmit = useCallback(
    (data: SubmitData) => {
      if (!currentAction || !ctId || !queryId || !siteId) return;
      updateCurrentQuery({
        clinicalTrialId: ctId,
        queryId,
        siteId,
        updateData: {
          event: currentAction,
          actedBy: uuid as string,
          message: data.message,
          attachments: data.attachments,
          ...(data.username &&
            data.password &&
            data.signAgreement && {
              userCredentials: {
                username: data.username,
                password: data.password,
                signAgreement: data.signAgreement,
              },
            }),
        },
      });
    },
    [ctId, queryId, siteId, currentAction, uuid, updateCurrentQuery]
  );

  return {
    handlers: {
      selectedRole: selectedRole as UserRoles,
      handleListQuerySubmit,
      updating,
    },
    handleUpdateCurrentQuery,
    handleDetailQuerySubmit,
  };
};
